import { Controller } from "@hotwired/stimulus"

/**
 * RT Exposures Controller
 * Manages radiography exposure setups with CRUD operations
 * Handles conditional fields based on Film/Digital method type
 * Uses explicit save pattern (not autosave)
 */
export default class extends Controller {
  static targets = [
    "exposureId",
    "reportId",
    "taskId",
    "methodType",
    "filmTypeId",
    "noOfFilms",
    "iqiTypeId",
    "reqSensitivity",
    "iqiPosition",
    "achSensitivityIqi",
    "exposureMethodId",

    "snrNormRequired",
    "snrNormAchieved",
    "srbImageRequired",
    "srbImageAchieved",
    "sfd",
    "exposureMins",
    "exposureSecs",
    "voltage",
    "current",
    "digitalFields",
    "saveButton",
    "saveButtonText",
    "alertBox",
    "exposuresContainer"
  ]

  connect() {
    console.log('[rt-exposures] Controller connected')
    this.isEditMode = false
  }

  /**
   * Save or update exposure
   */
  async saveExposure(event) {
    event.preventDefault()

    console.log('[rt-exposures] Save exposure clicked')

    // Validate required fields
    if (!this.validateFields()) {
      return
    }

    // Collect form data
    const exposureData = this.collectFormData()

    console.log('[rt-exposures] Exposure data:', exposureData)

    // Disable save button during request
    this.saveButtonTarget.disabled = true
    this.saveButtonTextTarget.textContent = this.isEditMode ? 'Updating...' : 'Saving...'

    try {
      const url = this.isEditMode
        ? `/tasks/${this.taskIdTarget.value}/reports/${this.reportIdTarget.value}/radiography_exposures/${this.exposureIdTarget.value}`
        : `/tasks/${this.taskIdTarget.value}/reports/${this.reportIdTarget.value}/radiography_exposures`

      const method = this.isEditMode ? 'PATCH' : 'POST'

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ radiography_exposure: exposureData })
      })

      const data = await response.json()

      if (response.ok && data.status === 'success') {
        console.log('[rt-exposures] Exposure saved successfully:', data)
        this.handleSaveSuccess(data)
      } else {
        console.error('[rt-exposures] Save failed:', data)
        this.showError(data.message || 'Failed to save exposure')
      }
    } catch (error) {
      console.error('[rt-exposures] Network error:', error)
      this.showError('Network error. Please try again.')
    } finally {
      this.saveButtonTarget.disabled = false
      this.saveButtonTextTarget.textContent = this.isEditMode ? 'Update Exposure' : 'Save Exposure'
    }
  }

  /**
   * Edit existing exposure
   */
  async editExposure(event) {
    event.preventDefault()

    const exposureId = event.currentTarget.dataset.exposureId

    console.log('[rt-exposures] Edit exposure:', exposureId)

    try {
      const response = await fetch(
        `/tasks/${this.taskIdTarget.value}/reports/${this.reportIdTarget.value}/radiography_exposures/${exposureId}.json`
      )

      if (!response.ok) throw new Error('Failed to fetch exposure data')

      const data = await response.json()

      console.log('[rt-exposures] Exposure data loaded:', data)

      // Populate form fields
      this.populateForm(data)

      // Set edit mode
      this.isEditMode = true
      this.exposureIdTarget.value = exposureId
      this.saveButtonTextTarget.textContent = 'Update Exposure'

      // Scroll to form
      this.element.querySelector('.card').scrollIntoView({ behavior: 'smooth', block: 'start' })
    } catch (error) {
      console.error('[rt-exposures] Error loading exposure:', error)
      this.showError('Failed to load exposure data')
    }
  }

  /**
   * Delete exposure
   */
  async deleteExposure(event) {
    event.preventDefault()

    const exposureId = event.currentTarget.dataset.exposureId

    if (!confirm('Are you sure you want to delete this exposure setup? Any welds linked to this exposure will lose their exposure reference.')) {
      return
    }

    console.log('[rt-exposures] Delete exposure:', exposureId)

    try {
      const response = await fetch(
        `/tasks/${this.taskIdTarget.value}/reports/${this.reportIdTarget.value}/radiography_exposures/${exposureId}`,
        {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': this.csrfToken,
            'Accept': 'application/json'
          }
        }
      )

      const data = await response.json()

      if (response.ok && data.status === 'success') {
        console.log('[rt-exposures] Exposure deleted')

        // Remove exposure card from DOM
        const exposureCard = this.exposuresContainerTarget.querySelector(`[data-exposure-id="${exposureId}"]`)
        if (exposureCard) {
          exposureCard.remove()
        }

        // If no exposures left, show empty state
        if (this.exposuresContainerTarget.querySelectorAll('[data-exposure-id]').length === 0) {
          this.showEmptyState()
        }
      } else {
        this.showError(data.message || 'Failed to delete exposure')
      }
    } catch (error) {
      console.error('[rt-exposures] Delete error:', error)
      this.showError('Network error. Please try again.')
    }
  }

  /**
   * Cancel edit mode
   */
  cancelEdit(event) {
    event.preventDefault()

    console.log('[rt-exposures] Cancel edit')

    this.clearForm()
    this.isEditMode = false
    this.exposureIdTarget.value = ''
    this.saveButtonTextTarget.textContent = 'Save Exposure'
  }

  /**
   * Collect form data
   */
  collectFormData() {
    const data = {
      film_type_id: this.filmTypeIdTarget.value || null,
      no_of_films: this.noOfFilmsTarget.value || null,
      iqi_type_id: this.iqiTypeIdTarget.value || null,
      req_sensitivity: this.reqSensitivityTarget.value,
      iqi_position: this.hasIqiPositionTarget ? this.iqiPositionTarget.value : null,
      ach_sensitivity_iqi: this.hasAchSensitivityIqiTarget ? this.achSensitivityIqiTarget.value : null,
      exposure_method_id: this.exposureMethodIdTarget.value || null,

      sfd: this.sfdTarget.value || null,
      exposure_mins: this.exposureMinsTarget.value || null,
      exposure_secs: this.exposureSecsTarget.value || null,
      voltage: this.voltageTarget.value,
      current: this.currentTarget.value
    }

    // Add digital-specific fields if digital method (kept for backward compatibility)
    if (this.methodTypeTarget.value === 'digital') {
      data.snr_norm_required = this.hasSnrNormRequiredTarget ? this.snrNormRequiredTarget.value : null
      data.snr_norm_achieved = this.hasSnrNormAchievedTarget ? this.snrNormAchievedTarget.value : null
      data.srb_image_required = this.hasSrbImageRequiredTarget ? this.srbImageRequiredTarget.value : null
      data.srb_image_achieved = this.hasSrbImageAchievedTarget ? this.srbImageAchievedTarget.value : null
    }

    return data
  }

  /**
   * Populate form with exposure data
   */
  populateForm(exposure) {
    this.noOfFilmsTarget.value = exposure.no_of_films || ''
    this.reqSensitivityTarget.value = exposure.req_sensitivity || ''
    this.sfdTarget.value = exposure.sfd || ''
    this.exposureMinsTarget.value = exposure.exposure_mins || ''
    this.exposureSecsTarget.value = exposure.exposure_secs || ''
    this.voltageTarget.value = exposure.voltage || ''
    this.currentTarget.value = exposure.current || ''

    // New IQI fields
    if (this.hasIqiPositionTarget) {
      this.iqiPositionTarget.value = exposure.iqi_position || ''
    }
    if (this.hasAchSensitivityIqiTarget) {
      this.achSensitivityIqiTarget.value = exposure.ach_sensitivity_iqi || ''
    }

    // Update Select2 dropdowns
    this.updateSelect2(this.filmTypeIdTarget, exposure.film_type_id)
    this.updateSelect2(this.iqiTypeIdTarget, exposure.iqi_type_id)
    this.updateSelect2(this.exposureMethodIdTarget, exposure.exposure_method_id)


    // Digital fields (kept for backward compatibility)
    if (this.hasSnrNormRequiredTarget) {
      this.snrNormRequiredTarget.value = exposure.snr_norm_required || ''
    }
    if (this.hasSnrNormAchievedTarget) {
      this.snrNormAchievedTarget.value = exposure.snr_norm_achieved || ''
    }
    if (this.hasSrbImageRequiredTarget) {
      this.srbImageRequiredTarget.value = exposure.srb_image_required || ''
    }
    if (this.hasSrbImageAchievedTarget) {
      this.srbImageAchievedTarget.value = exposure.srb_image_achieved || ''
    }
  }

  /**
   * Clear form fields
   */
  clearForm() {
    this.noOfFilmsTarget.value = ''
    this.reqSensitivityTarget.value = ''
    this.sfdTarget.value = ''
    this.exposureMinsTarget.value = ''
    this.exposureSecsTarget.value = ''
    this.voltageTarget.value = ''
    this.currentTarget.value = ''

    // Clear new IQI fields
    if (this.hasIqiPositionTarget) {
      this.iqiPositionTarget.value = ''
    }
    if (this.hasAchSensitivityIqiTarget) {
      this.achSensitivityIqiTarget.value = ''
    }

    // Clear Select2 dropdowns
    this.updateSelect2(this.filmTypeIdTarget, '')
    this.updateSelect2(this.iqiTypeIdTarget, '')
    this.updateSelect2(this.exposureMethodIdTarget, '')


    // Clear digital fields if present (kept for backward compatibility)
    if (this.hasSnrNormRequiredTarget) {
      this.snrNormRequiredTarget.value = ''
    }
    if (this.hasSnrNormAchievedTarget) {
      this.snrNormAchievedTarget.value = ''
    }
    if (this.hasSrbImageRequiredTarget) {
      this.srbImageRequiredTarget.value = ''
    }
    if (this.hasSrbImageAchievedTarget) {
      this.srbImageAchievedTarget.value = ''
    }

    this.hideError()
  }

  /**
   * Validate required fields
   */
  validateFields() {
    const missingFields = []

    // Check all required fields
    if (!this.filmTypeIdTarget.value) {
      missingFields.push('Film Type / Detector Type')
    }

    if (!this.exposureMethodIdTarget.value) {
      missingFields.push('Exposure Technique')
    }

    if (missingFields.length > 0) {
      this.showError(`Required fields missing: ${missingFields.join(', ')}`)
      // Focus on first empty required field
      if (!this.filmTypeIdTarget.value) {
        this.filmTypeIdTarget.focus()
      } else if (!this.exposureMethodIdTarget.value) {
        this.exposureMethodIdTarget.focus()
      }
      return false
    }

    return true
  }

  /**
   * Handle successful save
   */
  async handleSaveSuccess(data) {
    console.log('[rt-exposures] Save success, updating DOM')

    const exposureId = data.exposure.id

    // Build exposure object from initial response (optimistic update)
    // The controller now returns association names in the response
    const basicExposure = {
      id: exposureId,
      setup_code: data.exposure.setup_code || 'N/A',
      film_type_id: data.exposure.film_type_id,
      film_type_name: data.exposure.film_type_name || null,
      no_of_films: data.exposure.no_of_films || '',
      iqi_type_id: data.exposure.iqi_type_id,
      iqi_type_name: data.exposure.iqi_type_name || null,
      req_sensitivity: data.exposure.req_sensitivity || '',
      iqi_position: data.exposure.iqi_position || '',
      ach_sensitivity_iqi: data.exposure.ach_sensitivity_iqi || '',
      exposure_method_id: data.exposure.exposure_method_id,
      exposure_method_name: data.exposure.exposure_method_name || null,
      // Kept for backward compatibility
      snr_norm_required: data.exposure.snr_norm_required || '',
      snr_norm_achieved: data.exposure.snr_norm_achieved || '',
      srb_image_required: data.exposure.srb_image_required || '',
      srb_image_achieved: data.exposure.srb_image_achieved || '',
      sfd: data.exposure.sfd || '',
      exposure_mins: data.exposure.exposure_mins || '',
      exposure_secs: data.exposure.exposure_secs || '',
      voltage: data.exposure.voltage || '',
      current: data.exposure.current || ''
    }

    // Update DOM immediately with basic data
    this.updateExposureInDOM(basicExposure)

    // Try to fetch full data for better display (non-blocking enhancement)
    try {
      const response = await fetch(
        `/tasks/${this.taskIdTarget.value}/reports/${this.reportIdTarget.value}/radiography_exposures/${exposureId}/full.json`,
        { headers: { 'Accept': 'application/json' } }
      )
      if (response.ok) {
        const fullExposure = await response.json()
        this.updateExposureInDOM(fullExposure)
      }
      // If fetch fails, basic data is already displayed - no reload needed
    } catch (error) {
      console.warn('[rt-exposures] Could not fetch full data, using basic data from response')
      // DO NOT reload - basic data is already shown
    }

    // Clear form and reset state
    this.clearForm()
    this.isEditMode = false
    this.exposureIdTarget.value = ''
    this.saveButtonTextTarget.textContent = 'Save Exposure'

    // Scroll to the saved exposure and highlight the card
    const exposureCard = this.exposuresContainerTarget.querySelector(`[data-exposure-id="${exposureId}"]`)
    if (exposureCard) {
      exposureCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      exposureCard.style.transition = 'background-color 0.3s ease'
      exposureCard.style.backgroundColor = '#d4edda'
      setTimeout(() => {
        exposureCard.style.backgroundColor = ''
      }, 2000)
    }
  }

  /**
   * Update or create exposure card in DOM
   */
  updateExposureInDOM(exposure) {
    const existingCard = this.exposuresContainerTarget.querySelector(`[data-exposure-id="${exposure.id}"]`)

    // Remove empty state if it exists
    const emptyState = this.exposuresContainerTarget.querySelector('.text-center.text-muted')
    if (emptyState) {
      emptyState.remove()
    }

    const cardHtml = this.generateExposureCardHtml(exposure)

    if (existingCard) {
      // Update existing card
      existingCard.outerHTML = cardHtml
    } else {
      // Add new card at the beginning
      this.exposuresContainerTarget.insertAdjacentHTML('afterbegin', cardHtml)
    }
  }

  /**
   * Generate HTML for exposure card
   */
  generateExposureCardHtml(exposure) {
    return `
      <div class="exposure-card mb-3 p-3 border rounded" data-exposure-id="${exposure.id}">
        <div class="exposure-header mb-2">
          <div class="row align-items-center">
            <div class="col-md-8">
              <h6 class="mb-0">
                <span class="badge badge-dark mr-2" style="font-size: 14px;">
                  Setup: ${exposure.setup_code || 'N/A'}
                </span>
              </h6>
            </div>
            <div class="col-md-4 text-right">
              <button class="btn btn-sm btn-outline-primary mr-1" type="button" data-action="click->rt-exposures#editExposure" data-exposure-id="${exposure.id}" title="Edit">
                <i class="fa fa-edit"></i>
              </button>
              <button class="btn btn-sm btn-outline-danger" type="button" data-action="click->rt-exposures#deleteExposure" data-exposure-id="${exposure.id}" title="Delete">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>

        <hr class="my-2">

        <div class="row small">
          <div class="col-md-6">
            <div class="mb-1">
              <strong>Film Type:</strong>
              ${exposure.film_type_name || 'N/A'}
            </div>
            <div class="mb-1">
              <strong>No. of Exposures:</strong>
              ${exposure.no_of_films || 'N/A'}
            </div>
            <div class="mb-1">
              <strong>IQI Type / Sensitivity:</strong>
              ${exposure.iqi_type_name || 'N/A'}
            </div>
          </div>

          <div class="col-md-6">
            <div class="mb-1">
              <strong>IQI Position:</strong>
              ${exposure.iqi_position || 'N/A'}
            </div>
            <div class="mb-1">
              <strong>Req. Sensitivity / IQI:</strong>
              ${exposure.req_sensitivity || 'N/A'}
            </div>
            <div class="mb-1">
              <strong>Ach. Sensitivity / IQI:</strong>
              ${exposure.ach_sensitivity_iqi || 'N/A'}
            </div>
            <div class="mb-1">
              <strong>Exposure Technique:</strong>
              ${exposure.exposure_method_name || 'N/A'}
            </div>
            <div class="mb-1">
              <strong>SFD:</strong>
              ${exposure.sfd ? `${exposure.sfd}mm` : 'N/A'}
            </div>
            <div class="mb-1">
              <strong>Exp. Period:</strong>
              ${this.formatExposureTime(exposure.exposure_mins, exposure.exposure_secs)}
            </div>
            <div class="mb-1">
              <strong>kV & mA:</strong>
              ${(exposure.voltage || exposure.current) ? `${exposure.voltage || ''}kV - ${exposure.current || ''}mA` : 'N/A'}
            </div>
          </div>
        </div>
      </div>
    `
  }

  /**
   * Show empty state
   */
  showEmptyState() {
    this.exposuresContainerTarget.innerHTML = `
      <div class="text-center text-muted py-4">
        <i class="fa fa-camera mr-2" style="font-size: 48px;"></i>
        <p class="mt-2">No exposure setups created yet</p>
        <p class="small">Create an exposure setup using the form above</p>
      </div>
    `
  }

  /**
   * Format exposure time intelligently:
   * - Only seconds: "40 sec."
   * - Only minutes: "5 min."
   * - Both: "5 min. 40 sec."
   * - Neither: fallback (default 'N/A')
   */
  formatExposureTime(mins, secs, fallback = 'N/A') {
    mins = parseInt(mins) || 0
    secs = parseInt(secs) || 0
    if (mins === 0 && secs === 0) return fallback

    const parts = []
    if (mins > 0) parts.push(`${mins} min.`)
    if (secs > 0) parts.push(`${secs} sec.`)
    return parts.join(' ')
  }

  /**
   * Update Select2 dropdown value
   */
  updateSelect2(element, value) {
    if (!element) return

    element.value = value || ''

    // Update Select2 if initialized
    if (window.jQuery && window.jQuery(element).data('select2')) {
      window.jQuery(element).val(value || '').trigger('change')
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    if (!this.hasAlertBoxTarget) return

    this.alertBoxTarget.textContent = message
    this.alertBoxTarget.classList.remove('d-none')

    // Auto-hide after 5 seconds
    setTimeout(() => this.hideError(), 5000)
  }

  /**
   * Hide error message
   */
  hideError() {
    if (!this.hasAlertBoxTarget) return
    this.alertBoxTarget.classList.add('d-none')
  }

  /**
   * Get CSRF token
   */
  get csrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }
}
